import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Mirrors the device-agent-api wire protocol (@device-agent/shared). The browser talks to
// device-agent-api DIRECTLY for the live session, authed by the per-session capability token
// (NOT the Firebase token — see the design doc's auth model).

export interface DeviceSessionOutput {
  sessionId: string;
  sessionToken: string;
  streamUrl: string; // <publicUrl>/sessions/:sid/stream
  deviceName?: string; // friendly screen name for the header/card
  title?: string; // short symptom title (OptiBot-written); combined with deviceName for the label
  actor?: string; // who launched the session (customer/staff email) → "Started by …" in the header
  startedAt?: string; // set on history-rebuilt cards → shown to distinguish multiple sessions on one thread
}

/** "OptiStick Gen4 — Black screen" (device + symptom). Either part may be absent → returns the other, or
 *  undefined when neither is set (callers fall back to the generic "Device troubleshooting" label). */
export function sessionLabel(o: {
  deviceName?: string | null;
  title?: string | null;
}): string | undefined {
  const parts = [o.deviceName, o.title].filter((s): s is string => Boolean(s && s.trim()));
  return parts.length ? parts.join(' — ') : undefined;
}

/** The SSE event vocabulary (mirrors @device-agent/shared EventType). Const object + derived union — same
 *  pattern as ConnState — so checks read `ev.type === EventType.UserMessage` instead of magic strings. */
export const EventType = {
  SessionStarted: 'session.started',
  Diagnostic: 'diagnostic',
  Proposal: 'proposal',
  ApprovalRequest: 'approval_request',
  ApprovalGrant: 'approval_grant',
  Action: 'action',
  Verify: 'verify',
  UserMessage: 'user.message',
  UserInterrupt: 'user.interrupt',
  Status: 'status',
  Error: 'error',
  SessionEnded: 'session.ended',
} as const;
export type SessionEventType = (typeof EventType)[keyof typeof EventType];

export interface SessionEvent<P = unknown> {
  sessionId: string;
  seq: number;
  ts: string;
  type: SessionEventType;
  actor: 'runner' | 'user' | 'system';
  payload: P;
  schemaVersion: number;
}

/** Payload of a `diagnostic` event — the agent's streamed answer (ACP agent_message_chunk projection).
 *  Incremental deltas carry `final:false` and are APPENDED by `messageId`; the `final:true` chunk carries
 *  the complete coalesced text and REPLACES. A `snapshot:true` chunk is a reconnect/reload checkpoint whose
 *  `text` is cumulative — also REPLACES (so an EventSource auto-reconnect can't double the message). */
export interface DiagnosticPayload {
  text: string;
  messageId?: string;
  final?: boolean;
  snapshot?: boolean;
}

export interface ApprovalRequestPayload {
  toolUseId: string;
  tool: string;
  command: string;
  params: Record<string, unknown>;
  paramsHash: string;
  target: string;
  expiresAt: string;
  /** Standing-grant (D12) auto-approval: recorded for audit but resolved server-side — never render a card. */
  preAuthorized?: boolean;
  /** Agent-written explanation of what the command does and why (untrusted). */
  reason?: string;
}

export interface ApprovalGrant {
  toolUseId: string;
  decision: 'allow' | 'allow_with_changes' | 'deny';
  paramsHash: string;
  idempotencyKey: string;
  updatedInput?: Record<string, unknown>;
}

/** Server-reported session status (the collapsed vocabulary device-agent-api exposes — booting→creating,
 *  ending→ended are folded server-side). Const object + derived type so checks read
 *  `s.status === SessionStatus.Running` instead of magic strings. Values feed `optibot.session.status.<v>`. */
export const SessionStatus = {
  Creating: 'creating',
  Running: 'running',
  Paused: 'paused',
  Ended: 'ended',
  Failed: 'failed',
} as const;
export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

/** The 409 session-conflict codes device-agent-api returns on start/resume (surfaced by open-api at
 *  `extensions.exception.response.code`). Mirrors @device-agent/shared SessionConflictCode. Const object +
 *  derived type — same pattern as SessionStatus/ConnState — so the panel branches on
 *  `code === SessionConflictCode.Ending` instead of a magic string. */
export const SessionConflictCode = {
  AlreadyActive: 'SESSION_ALREADY_ACTIVE', // a live/creating session exists → offer to open it
  Ending: 'SESSION_ENDING', // a session is mid-teardown (End-then-Start/Resume race) → wait & retry
} as const;
export type SessionConflictCode = (typeof SessionConflictCode)[keyof typeof SessionConflictCode];

/** SSE connection state. Const object + derived type (matches the shared package's SessionStatus pattern)
 *  so checks read `conn === ConnState.Live` instead of magic strings. Values feed `optibot.session.conn.<v>`. */
export const ConnState = {
  Connecting: 'connecting',
  Live: 'live',
  Reconnecting: 'reconnecting',
  Ended: 'ended',
  Error: 'error',
} as const;
export type ConnState = (typeof ConnState)[keyof typeof ConnState];

/** Timeline render-unit kind. Const object + derived type (same pattern as EventType/ConnState) so checks
 *  read `item.kind === TimelineKind.Tool` instead of magic strings. */
export const TimelineKind = {
  Message: 'message', // a coalesced agent answer (diagnostic chunks folded by messageId)
  Tool: 'tool', // a device tool step (proposal + action/error folded by toolCallId)
  Event: 'event', // any other event, passed through
} as const;
export type TimelineKind = (typeof TimelineKind)[keyof typeof TimelineKind];

/** A tool step's lifecycle status — proposal seeds Running; its action/error completes it. */
export const ToolStatus = {
  Running: 'running',
  Ok: 'ok',
  Error: 'error',
} as const;
export type ToolStatus = (typeof ToolStatus)[keyof typeof ToolStatus];

/** A coalesced render unit. Streaming `diagnostic` chunks collapse into ONE `message` item per `messageId`
 *  (so the typewriter updates in place); a tool's `proposal` + later `action`/`error` collapse into ONE
 *  `tool` item per toolCallId (status + output land on the original command row); everything else is a
 *  passthrough `event`. */
export type TimelineItem =
  | { kind: typeof TimelineKind.Message; id: string; text: string; final: boolean; ts: string }
  | {
      kind: typeof TimelineKind.Tool;
      id: string;
      toolCallId: string;
      tool?: string; // raw tool name (mcp__device__…); rendered as a friendly label by the view
      command?: string; // params.command (read/exec)
      description?: string; // params.description: the agent's plain-language label for a read (untrusted)
      status: ToolStatus;
      output?: string;
      ts: string;
    }
  | { kind: typeof TimelineKind.Event; id: string; event: SessionEvent };

/** Fold the raw append-log into the render timeline. Pure + deterministic (rebuilt from scratch each
 *  change), so the append-vs-replace rule is applied consistently even across reconnects:
 *    - diagnostic delta (final:false, no snapshot) → APPEND text to its messageId item
 *    - diagnostic final / snapshot                 → REPLACE the item's text (snapshot = cumulative)
 *    - first sight of a messageId seeds the item (covers attach-after-finalize / attach-after-reconnect)
 *  A diagnostic without a messageId (legacy/atomic) and every non-diagnostic event passes through. */
export function buildTimeline(events: SessionEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  const indexByMsg = new Map<string, number>();
  const indexByTool = new Map<string, number>();
  for (const e of events) {
    const p = (e.payload ?? {}) as DiagnosticPayload;
    const mid =
      e.type === EventType.Diagnostic && typeof p.messageId === 'string' ? p.messageId : '';
    if (mid) {
      const text = typeof p.text === 'string' ? p.text : '';
      const idx = indexByMsg.get(mid);
      if (idx === undefined) {
        indexByMsg.set(mid, items.length);
        items.push({
          kind: TimelineKind.Message,
          id: mid,
          text,
          final: p.final === true,
          ts: e.ts,
        });
      } else {
        const item = items[idx] as Extract<TimelineItem, { kind: typeof TimelineKind.Message }>;
        if (p.final === true || p.snapshot === true)
          item.text = text; // replace checkpoint
        else item.text += text; // incremental delta
        if (p.final === true) item.final = true;
        item.ts = e.ts; // advance to the latest contributing chunk (ends at the final's time)
      }
      continue;
    }

    // Tool-step fold (same idea as the diagnostic fold above, keyed by toolCallId): a `proposal` (the
    // command) and its later `action`/`error` (status + output) collapse into ONE row, so the ✓/output lands
    // ON the original command line instead of appending a separate item far below. The agent fires many reads
    // in parallel, so without this all commands stream first and all results after, visually disconnected.
    const tp = e.payload as {
      toolCallId?: string;
      title?: string;
      params?: { command?: unknown; description?: unknown };
      output?: string;
    } | null;
    const isToolStep =
      e.type === EventType.Proposal || e.type === EventType.Action || e.type === EventType.Error;
    if (isToolStep && tp?.toolCallId) {
      const tid = tp.toolCallId;
      const command = typeof tp.params?.command === 'string' ? tp.params.command : undefined;
      const description =
        typeof tp.params?.description === 'string' && tp.params.description.trim()
          ? tp.params.description.trim()
          : undefined;
      const output = typeof tp.output === 'string' && tp.output.trim() ? tp.output : undefined;
      const idx = indexByTool.get(tid);
      if (idx === undefined) {
        indexByTool.set(tid, items.length);
        items.push({
          kind: TimelineKind.Tool,
          id: tid,
          toolCallId: tid,
          tool: tp.title,
          command,
          description,
          status:
            e.type === EventType.Action
              ? ToolStatus.Ok
              : e.type === EventType.Error
                ? ToolStatus.Error
                : ToolStatus.Running,
          output,
          ts: e.ts,
        });
      } else {
        const item = items[idx] as Extract<TimelineItem, { kind: typeof TimelineKind.Tool }>;
        if (tp.title) item.tool = tp.title; // proposal carries the name
        if (command) item.command = command; // proposal carries the command
        if (description) item.description = description;
        if (output) item.output = output; // action/error carries the output
        // action/error completes the step; a (late/duplicate) proposal must NOT downgrade a finished status.
        if (e.type === EventType.Action) item.status = ToolStatus.Ok;
        else if (e.type === EventType.Error) item.status = ToolStatus.Error;
        item.ts = e.ts;
      }
      continue;
    }

    items.push({ kind: TimelineKind.Event, id: `e${e.seq}-${e.type}`, event: e });
  }
  return items;
}

const EVENT_TYPES: SessionEventType[] = Object.values(EventType);

/** Streams a device-troubleshoot session over SSE (live) or renders a static transcript (history).
 *  Live: native EventSource (token in the URL — EventSource can't set headers); dedup by seq; the
 *  browser's built-in Last-Event-ID resume covers reconnects. Returns the timeline + approve/interrupt. */
export function useDeviceSessionStream(
  session: DeviceSessionOutput,
  opts?: { mode?: 'history' | 'live'; initialEvents?: SessionEvent[] },
) {
  const mode = opts?.mode ?? 'live';
  // Seed the timeline with the full transcript AS-IS, INCLUDING any prior-life `session.ended`. On a resumed
  // session those terminals are part of the record — they render as event rows so the user sees where each
  // life ended/restarted (one continuous thread). It's safe to keep them: a SEEDED `session.ended` never flips
  // the connection to Ended (only SSE-delivered events run through onEvent), and the SSE replay is floored to
  // the CURRENT life (lifeStartSeq) so it can't re-deliver a prior terminal. The view treats a live session
  // whose tail is a session.ended as "resuming" (spinner), so the seam never looks like a dead end.
  const initial = opts?.initialEvents ?? [];
  const [events, setEvents] = useState<SessionEvent[]>(initial);
  const [conn, setConn] = useState<ConnState>(
    mode === 'history' ? ConnState.Ended : ConnState.Connecting,
  );
  const [busy, setBusy] = useState(false); // agent mid-turn (active) vs idle/waiting — from the runner heartbeat
  const seen = useRef<Set<number>>(new Set(initial.map((e) => e.seq)));
  // Replay cursor (max durable seq seen) + a manual-reconnect trigger. A FRESH EventSource (manual reconnect
  // or first mount) can't set the Last-Event-ID *header*, so we pass the cursor as a query param — the api
  // replays only events after it (stream.controller: `id:` == seq). The browser's own auto-reconnect uses the
  // header instead; both resume the SAME live session, so a transient SSE drop is recoverable, never a
  // dead-end (the session lives on the runner regardless of the browser connection).
  const lastSeqRef = useRef<number>(initial.reduce((m, e) => Math.max(m, e.seq), 0));
  const [reconnectNonce, setReconnectNonce] = useState(0);

  // History mode has NO live SSE appends (the effect below early-returns for non-live), so keep the event log
  // in sync with the transcript. `events` is seeded from `initialEvents` only ONCE (useState initial), so a
  // transcript that finishes loading (or updates) AFTER this hook mounted would otherwise never appear —
  // leaving the view stuck on "Connecting to the device…" for a session that actually HAS recorded activity
  // (e.g. a live-but-empty session you navigated away from, reopened as history once it accumulated events).
  useEffect(() => {
    const seed = opts?.initialEvents ?? [];
    if (mode === 'history' && seed.length > 0) setEvents(seed);
  }, [mode, opts?.initialEvents]);

  useEffect(() => {
    if (mode !== 'live') return;
    const cursor = lastSeqRef.current;
    const url =
      `${session.streamUrl}?token=${encodeURIComponent(session.sessionToken)}` +
      (cursor > 0 ? `&lastEventId=${cursor}` : '');
    const es = new EventSource(url);

    // Bound reconnects. Native EventSource retries forever and hides the HTTP status, so an
    // invalid/ended session (bad token, 4xx, runner gone, api restarted) would otherwise spin
    // "Reconnecting…" indefinitely. Two stops: (1) a CLOSED readyState = permanent failure → error
    // now; (2) a deadline timer caps how long we'll keep trying transient reconnects → error.
    // The Error state is NOT a dead-end — `reconnect()` re-opens from the replay cursor (the session is
    // still alive on the runner), so this deadline only governs how long we AUTO-heal before asking the user.
    const RECONNECT_DEADLINE_MS = 60_000;
    let giveUp: ReturnType<typeof setTimeout> | undefined;
    const clearGiveUp = () => {
      if (giveUp) clearTimeout(giveUp);
      giveUp = undefined;
    };
    const stopWithError = () => {
      clearGiveUp();
      es.close();
      setConn((prev) => (prev === ConnState.Ended ? prev : ConnState.Error));
    };

    // "Live" means a RUNNER is attached — NOT merely that the SSE reached our api. es.onopen only proves the
    // browser connected to device-agent-api (always up, even while the device's runner is still booting or has
    // failed to attach), so flipping to Live there showed a phantom "Live" with no runner behind it (composer
    // enabled → the user's messages get published to an empty channel and dropped). The BACKEND owns the
    // status; we reflect it: stay "Connecting…" until the runner actually SIGNALS — a `status` heartbeat, which
    // the api emits the instant the runner attaches (and every ~15s after). A boot that never attaches surfaces
    // via `session.ended` (reaper → failed) or, as a pure fallback, the attach deadline below.
    const ATTACH_DEADLINE_MS = 130_000; // > the api's ~120s boot grace, so session.ended normally wins first
    let sawRunner = false;
    let attachTimer: ReturnType<typeof setTimeout> | undefined;
    const clearAttach = () => {
      if (attachTimer) clearTimeout(attachTimer);
      attachTimer = undefined;
    };
    const markLive = () => {
      sawRunner = true;
      clearAttach();
      setConn((prev) => (prev === ConnState.Ended ? prev : ConnState.Live));
    };

    const onEvent = (e: MessageEvent) => {
      let ev: SessionEvent;
      try {
        ev = JSON.parse(e.data) as SessionEvent;
      } catch {
        return;
      }
      // Transient agent busy/idle (heartbeat-driven) — drives the "Working…" indicator, not the timeline.
      // It also proves a RUNNER is attached (the api emits one on attach + every ~15s) → this is our "Live".
      if (ev.type === EventType.Status) {
        markLive();
        setBusy(Boolean((ev.payload as { busy?: boolean } | null)?.busy));
        return;
      }
      // Optimistic "working" kick: the heartbeat (status) is the authority but only fires ~every 15s, so
      // flip to working the INSTANT the agent produces output — mirrors the CLI spinner that starts on
      // activity, not on a poll. The next heartbeat confirms (stays busy) or clears it (turn ended). A
      // final diagnostic is the END of an answer, so it doesn't kick (avoids a stale "working" tail).
      const producing =
        ev.type === EventType.Proposal ||
        ev.type === EventType.Action ||
        ev.type === EventType.Verify ||
        (ev.type === EventType.Diagnostic &&
          (ev.payload as DiagnosticPayload | null)?.final !== true);
      if (producing) setBusy(true);
      // Dedup durable events (seq>0) on replay/overlap. Streaming chunks are seq=0 (ephemeral, no SSE id) —
      // never seq-dedup them (every delta shares seq=0); the timeline fold coalesces them by messageId, and
      // a re-sent cumulative `snapshot` replaces rather than doubles.
      if (ev.seq > 0) {
        if (seen.current.has(ev.seq)) return;
        seen.current.add(ev.seq);
        if (ev.seq > lastSeqRef.current) lastSeqRef.current = ev.seq; // advance the replay cursor
      }
      setEvents((prev) => [...prev, ev]);
      if (ev.type === EventType.SessionEnded) {
        clearGiveUp();
        clearAttach();
        setConn(ConnState.Ended);
        es.close();
      }
    };

    es.onopen = () => {
      clearGiveUp(); // a live reconnect cancels the give-up deadline
      // SSE reached our api — but that is NOT "a runner is attached". If we've already seen the runner this
      // connection (a transient auto-reconnect to a live session), stay Live; otherwise sit in "Connecting…"
      // until the runner's first `status` signal (markLive), with a fallback deadline for a boot that never
      // attaches AND never emits session.ended.
      if (sawRunner) {
        setConn(ConnState.Live);
        return;
      }
      setConn(ConnState.Connecting);
      if (!attachTimer) {
        attachTimer = setTimeout(() => {
          setConn((prev) =>
            prev === ConnState.Ended || prev === ConnState.Live ? prev : ConnState.Error,
          );
        }, ATTACH_DEADLINE_MS);
      }
    };
    es.onerror = () => {
      // Permanent failure — the browser won't auto-reconnect (e.g. non-2xx / bad token): surface now.
      if (es.readyState === EventSource.CLOSED) {
        stopWithError();
        return;
      }
      // Transient — EventSource is auto-reconnecting. Show it, but arm a one-shot deadline (from the
      // first drop) so we don't reconnect forever against a session that's gone.
      setConn((prev) => (prev === ConnState.Ended ? prev : ConnState.Reconnecting));
      if (!giveUp) giveUp = setTimeout(stopWithError, RECONNECT_DEADLINE_MS);
    };
    // Bind every type EXCEPT `error`: EventSource reserves the `error` event name for connection failures, so
    // the api sends `error` events as a default `message` frame instead (see stream.controller). Binding
    // `error` here would also fire onEvent on REAL connection drops (which have no `.data`). Error events —
    // and any future reserved-name types — arrive via es.onmessage below.
    EVENT_TYPES.filter((t) => t !== EventType.Error).forEach((t) =>
      es.addEventListener(t, onEvent as EventListener),
    );
    es.onmessage = onEvent; // default `message` frames: error events (+ any reserved-name type) carry their real type in the JSON

    return () => {
      clearGiveUp();
      clearAttach();
      es.close();
    };
  }, [session.streamUrl, session.sessionToken, mode, reconnectNonce]);

  // Manual resume after a drop. Re-opens the EventSource from the replay cursor — the session lives on the
  // runner independent of the browser connection, so this picks up exactly where it left off (the api
  // replays everything after `lastSeqRef`; `seen` dedups any overlap). The recovery for a transient blip
  // the 60s auto-deadline didn't outlast, so the user is never stranded on a still-running session.
  const reconnect = useCallback(() => {
    setConn(ConnState.Connecting);
    setReconnectNonce((n) => n + 1);
  }, []);

  const post = useCallback(
    (path: 'approve' | 'interrupt' | 'message' | 'end', body?: unknown): Promise<Response> => {
      const base = session.streamUrl.split('/sessions/')[0]; // <publicUrl>
      return fetch(`${base}/sessions/${session.sessionId}/${path}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${session.sessionToken}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    },
    [session.streamUrl, session.sessionId, session.sessionToken],
  );

  const decide = useCallback(
    (req: ApprovalRequestPayload, decision: 'allow' | 'deny'): Promise<void> => {
      if (decision === 'allow') setBusy(true); // optimistic — the agent resumes the moment the grant lands
      return post('approve', {
        toolUseId: req.toolUseId,
        decision,
        paramsHash: req.paramsHash,
        idempotencyKey: `${req.toolUseId}:${decision}`, // single-use; the notary rejects replays
      } satisfies ApprovalGrant).then(() => {});
    },
    [post],
  );

  const interrupt = useCallback((): Promise<void> => post('interrupt').then(() => {}), [post]);

  // End the session — tears down the runner (deprovision peer, emit session.ended). We optimistically flip
  // conn → 'ended' as soon as the POST succeeds: the api tears the runner + SSE down on this call, and the
  // terminating stream can close before its `session.ended` frame reaches the browser — so waiting on that
  // round-trip left the card stuck showing "Live" (the reported "End doesn't work"). The `session.ended`
  // event, if it does arrive, coalesces (already Ended). Throws on a non-2xx so the caller can surface it.
  const endSession = useCallback(async (): Promise<void> => {
    const res = await post('end');
    if (!res.ok) throw new Error(`end session failed (${res.status})`);
    setConn(ConnState.Ended);
  }, [post]);

  // Steer the live agent: the device-agent records a `user.message` event (streamed back over SSE, so it
  // lands in the timeline) and feeds the text to the running session. Only meaningful while `conn==='live'`.
  const sendMessage = useCallback(
    (text: string): Promise<void> => {
      setBusy(true); // optimistic — the agent starts on this immediately (don't wait for the next heartbeat)
      return post('message', { text }).then(() => {});
    },
    [post],
  );

  // Coalesced render units (streaming diagnostics folded into one message each); `events` stays the raw log.
  const timeline = useMemo(() => buildTimeline(events), [events]);

  return { events, timeline, conn, busy, decide, interrupt, endSession, sendMessage, reconnect };
}
